import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import SVGIcon from 'src/components/ui/SVGIcon';

import { BOX_SHADOW } from 'src/vars';

const timeOptions = [
    { label: 'x0.2', factor: 0.2 },
    { label: 'x0.5', factor: 0.5 },
    { label: 'x1', factor: 1 },
    { label: 'x1.5', factor: 1.5 },
    { label: 'x2', factor: 2 },
];

const TimeMenu = styled.div`
    position: absolute;
    bottom: calc(100% + 4px);
    left: 0;
    z-index: 10;
    min-width: 84px;
    padding: 6px;
    border-radius: 8px;
    background: var(--surface);
    box-shadow: ${BOX_SHADOW};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
    transform: translateY(${({ $isOpen }) => ($isOpen ? 0 : '4px')});
    transition: opacity 120ms ease, transform 120ms ease, visibility 120ms ease;
`;

const TimeMenuWrapper = styled.div`
    position: relative;
    display: inline-flex;
    align-items: center;

    &::after {
        position: absolute;
        bottom: 100%;
        right: 0;
        left: 0;
        height: 4px;
        content: '';
    }
`;

const TimeMenuTrigger = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-width: 72px;
    padding: 4px 8px;
    font-size: 14px;
    line-height: 18px;
    font-weight: 700;
    color: var(--poki-blue);
    background: none;
    border: 1px solid var(--border-faint);
    border-radius: 4px;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        border-color: var(--poki-blue);
        color: var(--poki-blue);
    }

    &:focus {
        outline: none;
        border-color: var(--poki-blue);
        color: var(--poki-blue);
    }
`;

const TimeMenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    border: none;
    border-radius: 6px;
    background: ${({ $isActive }) => ($isActive ? 'var(--surface-raised)' : 'transparent')};
    color: ${({ $isActive }) => ($isActive ? 'var(--poki-blue)' : 'var(--text-primary)')};
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;

    &:hover,
    &:focus {
        background: var(--surface-raised);
        outline: none;
    }
`;

const ActiveIndicator = styled(SVGIcon)`
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
    color: ${({ $isActive }) => ($isActive ? 'var(--poki-blue)' : 'var(--text-primary)')};
`;

const TimeMenuItemWrapper = styled.div`
    width: 100%;
`;

export default function TimeToggle() {
    const [selectedFactor, setSelectedFactor] = useState(1);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [gameIsPaused, setGameIsPaused] = useState(false);
    const menuWrapperRef = useRef(null);
    const selectedFactorRef = useRef(selectedFactor);

    const setTime = (factor) => {
        const iframe = document.querySelector('iframe');

        iframe?.contentWindow?.postMessage({ type: 'pokiSetTimeFactor', factor }, '*');
    };

    const handleOptionClick = (factor) => {
        selectedFactorRef.current = factor;
        setSelectedFactor(factor);
        setIsMenuOpen(false);

        if (!gameIsPaused) {
            setTime(factor);
        }
    };

    const handleMenuBlur = (event) => {
        if (menuWrapperRef.current?.contains(event.relatedTarget)) {
            return;
        }

        setIsMenuOpen(false);
    };

    useEffect(() => {
        selectedFactorRef.current = selectedFactor;
    }, [selectedFactor]);

    useEffect(() => {
        const postSelectedFactor = factor => {
            if (factor === undefined) {
                factor = selectedFactorRef.current;
            }

            setTime(factor);
        };

        const onBlur = () => {
            if (document.hidden) {
                setGameIsPaused(true);
                postSelectedFactor(0);
                return;
            }

            setGameIsPaused(false);
            postSelectedFactor();
        };

        const onFocus = () => {
            // When the Inspector gets focus we want to pause the game as it will loose Keyboard focus.
            setGameIsPaused(true);
            postSelectedFactor(0);
        };

        window.addEventListener('blur', onBlur);
        window.addEventListener('focus', onFocus);

        return () => {
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    return (
        <TimeMenuWrapper
            ref={menuWrapperRef}
            onMouseEnter={() => setIsMenuOpen(true)}
            onMouseLeave={() => setIsMenuOpen(false)}
            onFocus={() => setIsMenuOpen(true)}
            onBlur={handleMenuBlur}
        >
            <TimeMenuTrigger type="button" aria-haspopup="menu" aria-expanded={isMenuOpen}>
                <ActiveIndicator icon="clock" $isActive={true} />
                {timeOptions.find((option) => option.factor === selectedFactor).label}
            </TimeMenuTrigger>
            <TimeMenu role="menu" $isOpen={isMenuOpen}>
                {timeOptions.map(({ label, factor }) => {
                    const isActive = factor === selectedFactor;

                    return (
                        <TimeMenuItemWrapper key={factor}>
                            <TimeMenuItem
                                type="button"
                                role="menuitemradio"
                                aria-checked={isActive}
                                $isActive={isActive}
                                onClick={() => handleOptionClick(factor)}
                            >
                                <ActiveIndicator icon="clock" $isActive={isActive} />
                                {label}
                            </TimeMenuItem>
                        </TimeMenuItemWrapper>
                    );
                })}
            </TimeMenu>
        </TimeMenuWrapper>
    );
}
